import { helpWhy } from '../../help/why';
import React, { useCallback, useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { lcFlow } from '../../help/flows';
import { B2B_LC_STAGES } from '../../help/statusStages';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { AuthService } from '../../services/auth.service';
import { BackToBackLcService } from '../../services/backToBackLc.service';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';
import { B2bAccountingService } from '../../services/b2bAccounting.service';
import { PERMISSIONS } from '../../constants/permissions';
import {
    B2bAcceptance, B2bAcceptanceStatus, B2bLcAccounting, BackToBackLc,
    CreateB2bAcceptancePaymentRequest, CreateB2bAcceptanceRequest, SetB2bMarginRequest,
} from '../../interfaces/commercial.interface';
import { formatMoney } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { enumLabel } from '../../utils/garmentsLabels';

const STATUS_COLOR: Record<B2bAcceptanceStatus, 'default' | 'warning' | 'success'> = {
    ACCEPTED: 'default', PARTIALLY_PAID: 'warning', PAID: 'success',
};

const journalLink = (id: string | null | undefined, label: string) =>
    id ? (
        <Link component={RouterLink} to={`/accounting/journal-entries/${id}`} underline="hover" sx={{ mr: 1 }}>
            {label}
        </Link>
    ) : null;

/**
 * Back-to-back LC accounting detail (accounting wave 2): margin held by the bank, bank acceptances
 * of import documents (liability at the acceptance rate) and the maturity payments that settle them,
 * each with its ledger journal (linked only for users who may view journal entries).
 */
const B2bLcDetail: React.FC = () => {
    const { masterLcId = '', b2bId = '' } = useParams<{ masterLcId: string; b2bId: string }>();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.LC_MANAGE);
    const canViewJournal = usePermission(PERMISSIONS.JOURNAL_ENTRY_VIEW);
    const institutionId = AuthService.getSelectedInstitution() ?? '';

    const [lc, setLc] = useState<BackToBackLc | null>(null);
    const [acc, setAcc] = useState<B2bLcAccounting | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);

    const [marginOpen, setMarginOpen] = useState(false);
    const [margin, setMargin] = useState({ marginPercent: '', marginAmount: '', marginDate: toLocalIsoDate(), bankAccountId: '' });
    const [releaseOpen, setReleaseOpen] = useState(false);
    const [releaseDate, setReleaseDate] = useState(toLocalIsoDate());
    const [acceptOpen, setAcceptOpen] = useState(false);
    const [accept, setAccept] = useState({ acceptanceDate: toLocalIsoDate(), billReference: '', foreignAmount: '', acceptanceRate: '', usanceDays: '90', remarks: '' });
    const [payTarget, setPayTarget] = useState<B2bAcceptance | null>(null);
    const [pay, setPay] = useState({ paymentDate: toLocalIsoDate(), foreignAmount: '', paymentRate: '', bankCharges: '', reference: '', paymentAccountId: '' });

    const load = useCallback(async () => {
        if (!institutionId) return;
        try {
            setLoading(true);
            const [lcRes, accRes] = await Promise.all([
                BackToBackLcService.getById(institutionId, masterLcId, b2bId),
                B2bAccountingService.get(institutionId, masterLcId, b2bId),
            ]);
            setLc(lcRes);
            setAcc(accRes);
        } catch (e) {
            toast.showApiError(e, 'Failed to load back-to-back LC accounting');
        } finally {
            setLoading(false);
        }
    }, [institutionId, masterLcId, b2bId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    // Bank-account pickers only matter to someone who can post; a view-only user may lack chart access.
    useEffect(() => {
        if (!canManage) return;
        AccountService.getAllAccounts().then(a => setAccounts(a ?? [])).catch(() => setAccounts([]));
    }, [canManage]);

    const run = async (fn: () => Promise<unknown>, okMessage: string, after?: (result?: unknown) => void, onResult?: (result?: unknown) => void) => {
        try {
            setBusy(true);
            const result = await fn();
            toast.showSuccess(okMessage);
            after?.();
            onResult?.(result);
            await load();
        } catch (e) {
            toast.showApiError(e, 'The request failed');
        } finally {
            setBusy(false);
        }
    };

    if (loading && !lc) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    if (!lc || !acc) return <Box sx={{ p: 3 }}><Alert severity="error">Back-to-back LC not found.</Alert></Box>;

    const cur = lc.currencyCode;
    const m = acc.margin;
    const marginHeld = !!m.marginJournalEntryId;
    const marginReleased = !!m.marginReleaseJournalEntryId;
    const closed = lc.status === 'CLOSED';

    const saveMargin = () => {
        const dto: SetB2bMarginRequest = {
            marginPercent: margin.marginPercent === '' ? null : Number(margin.marginPercent),
            marginAmount: Number(margin.marginAmount),
            marginDate: margin.marginDate,
            bankAccountId: margin.bankAccountId || null,
        };
        run(() => B2bAccountingService.setMargin(institutionId, masterLcId, b2bId, dto), 'Margin recorded', () => setMarginOpen(false));
    };

    const saveAcceptance = () => {
        const dto: CreateB2bAcceptanceRequest = {
            acceptanceDate: accept.acceptanceDate, billReference: accept.billReference.trim(),
            foreignAmount: Number(accept.foreignAmount), acceptanceRate: Number(accept.acceptanceRate),
            usanceDays: Number(accept.usanceDays), remarks: accept.remarks.trim() || undefined,
        };
        run(() => B2bAccountingService.createAcceptance(institutionId, masterLcId, b2bId, dto), 'Acceptance recorded', () => setAcceptOpen(false), r => setWarnings((r as B2bAcceptance | undefined)?.warnings ?? []));
    };

    const savePayment = () => {
        if (!payTarget) return;
        const dto: CreateB2bAcceptancePaymentRequest = {
            paymentDate: pay.paymentDate, foreignAmount: Number(pay.foreignAmount), paymentRate: Number(pay.paymentRate),
            bankCharges: pay.bankCharges === '' ? undefined : Number(pay.bankCharges),
            reference: pay.reference.trim() || undefined,
            paymentAccountId: pay.paymentAccountId || null,
        };
        run(() => B2bAccountingService.createPayment(institutionId, masterLcId, b2bId, payTarget.id, dto), 'Payment recorded', () => setPayTarget(null));
    };

    const openPay = (a: B2bAcceptance) => {
        setPay({ paymentDate: toLocalIsoDate(), foreignAmount: String(a.outstandingForeignAmount), paymentRate: cur === 'BDT' ? '1' : '', bankCharges: '', reference: '', paymentAccountId: '' });
        setPayTarget(a);
    };

    const bankAccounts = leafAccounts(accounts).filter(a => a.accountType === 'ASSET');

    const marginValid = Number(margin.marginAmount) > 0 && !!margin.marginDate;
    const acceptValid = !!accept.billReference.trim() && Number(accept.foreignAmount) > 0 && Number(accept.acceptanceRate) > 0
        && Number(accept.usanceDays) >= 0 && !!accept.acceptanceDate;
    const payValid = !!payTarget && Number(pay.foreignAmount) > 0 && Number(pay.foreignAmount) <= payTarget.outstandingForeignAmount
        && Number(pay.paymentRate) > 0 && !!pay.paymentDate;

    const marginWhy = busy ? null
        : !(Number(margin.marginAmount) > 0) ? 'Enter a margin amount above zero.'
        : !margin.marginDate ? 'Enter the margin date.'
        : null;

    const releaseWhy = busy ? null
        : !releaseDate ? 'Enter the release date.'
        : null;

    const accWhy = busy ? null
        : !accept.billReference.trim() ? 'Enter the bill reference.'
        : !(Number(accept.foreignAmount) > 0) ? 'Enter a foreign amount above zero.'
        : !(Number(accept.acceptanceRate) > 0) ? 'Enter an acceptance rate above zero.'
        : !(Number(accept.usanceDays) >= 0) ? 'Enter the usance days, zero or more.'
        : !accept.acceptanceDate ? 'Enter the acceptance date.'
        : null;

    const payWhy = busy ? null
        : !payTarget ? 'Pick the acceptance to pay.'
        : !(Number(pay.foreignAmount) > 0) ? 'Enter a foreign amount above zero.'
        : payTarget && Number(pay.foreignAmount) > payTarget.outstandingForeignAmount ? 'The amount cannot be more than the outstanding foreign amount.'
        : !(Number(pay.paymentRate) > 0) ? 'Enter a payment rate above zero.'
        : !pay.paymentDate ? 'Enter the payment date.'
        : null;

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.b2bDetail.page">
            <FlowStrip data-help="commercial.b2b.flow" stages={lcFlow({ current: 'btb', lc: { id: lc.masterLcId }, btb: { no: lc.b2bLcNumber } })} />
            <Typography variant="h5" sx={{ mb: 0.5 }}>Back-to-Back LC {lc.b2bLcNumber}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {lc.supplierName} · {lc.issuingBank} · {formatMoney(lc.amount, cur)} · <StatusStagesChip data-help="commercial.b2b.statusStages" label={enumLabel(lc.status)} status={lc.status} stages={B2B_LC_STAGES} color={'default'} />
                {' '}<Link component={RouterLink} to={`/commercial/lc/${lc.masterLcId}`}>Master LC</Link>
            </Typography>

            {warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="acceptance-warnings" onClose={() => setWarnings([])}>
                    {warnings.map(w => <div key={w}>{w}</div>)}
                </Alert>
            )}

            <Paper data-help-section="commercial.b2bDetail.margin" sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>LC margin</Typography>
                {!marginHeld ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">No margin recorded.</Typography>
                        {canManage && !closed && <Button data-help="commercial.b2bDetail.recordMargin" size="small" variant="outlined" onClick={() => setMarginOpen(true)}>Record margin</Button>}
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="body2">
                            {formatMoney(m.marginAmount, 'BDT')}{m.marginPercent != null ? ` (${m.marginPercent}%)` : ''} taken on {formatDate(m.marginDate!)}
                            {marginReleased ? `, released on ${formatDate(m.marginReleaseDate!)}` : ', held by the bank'}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {canViewJournal && journalLink(m.marginJournalEntryId, 'Margin journal')}
                            {canViewJournal && journalLink(m.marginReleaseJournalEntryId, 'Release journal')}
                            {canManage && !marginReleased && <Button data-help="commercial.b2bDetail.releaseMargin" size="small" variant="outlined" onClick={() => { setReleaseDate(toLocalIsoDate()); setReleaseOpen(true); }}>Release margin</Button>}
                            {canManage && !marginReleased && <Button data-help="commercial.b2bDetail.removeMargin" size="small" color="error" disabled={busy}
                                onClick={() => run(() => B2bAccountingService.removeMargin(institutionId, masterLcId, b2bId), 'Margin reversed')}>Remove margin</Button>}
                            {canManage && marginReleased && <Button data-help="commercial.b2bDetail.reverseRelease" size="small" color="error" disabled={busy}
                                onClick={() => run(() => B2bAccountingService.reverseMarginRelease(institutionId, masterLcId, b2bId), 'Release reversed')}>Reverse release</Button>}
                        </Box>
                    </Box>
                )}
            </Paper>

            <Paper data-help-section="commercial.b2bDetail.acceptances" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="h6">Acceptances</Typography>
                    {canManage && !closed && <Button data-help="commercial.b2bDetail.recordAcceptance" variant="contained" size="small" onClick={() => setAcceptOpen(true)}>Record acceptance</Button>}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Accepted {formatMoney(acc.acceptedForeign, cur)} · paid {formatMoney(acc.paidForeign, cur)} · outstanding {formatMoney(acc.outstandingForeign, cur)}
                    {' '}({formatMoney(acc.outstandingBase, 'BDT')} liability)
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.b2bDetail.col.bill">Bill</TableCell>
                                <TableCell data-help="commercial.b2bDetail.col.accepted">Accepted</TableCell>
                                <TableCell align="right" data-help="commercial.b2bDetail.col.amount">Amount</TableCell>
                                <TableCell align="right" data-help="commercial.b2bDetail.col.rate">Rate</TableCell>
                                <TableCell align="right" data-help="commercial.b2bDetail.col.base">BDT</TableCell>
                                <TableCell data-help="commercial.b2bDetail.col.maturity">Maturity</TableCell>
                                <TableCell align="right" data-help="commercial.b2bDetail.col.outstanding">Outstanding</TableCell>
                                <TableCell data-help="commercial.b2bDetail.col.status">Status</TableCell>
                                <TableCell data-help="commercial.b2bDetail.col.actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {acc.acceptances.map(a => (
                                <React.Fragment key={a.id}>
                                    <TableRow>
                                        <TableCell>{a.billReference}</TableCell>
                                        <TableCell>{formatDate(a.acceptanceDate)}</TableCell>
                                        <TableCell align="right">{formatMoney(a.foreignAmount, cur)}</TableCell>
                                        <TableCell align="right">{a.acceptanceRate}</TableCell>
                                        <TableCell align="right">{formatMoney(a.baseAmount, 'BDT')}</TableCell>
                                        <TableCell>{formatDate(a.maturityDate)} ({a.usanceDays}d){a.overdue && <Chip size="small" color="error" label="Overdue" sx={{ ml: 1 }} />}</TableCell>
                                        <TableCell align="right">{formatMoney(a.outstandingForeignAmount, cur)}</TableCell>
                                        <TableCell><Chip size="small" color={STATUS_COLOR[a.status]} label={enumLabel(a.status)} /></TableCell>
                                        <TableCell>
                                            {canViewJournal && journalLink(a.journalEntryId, 'Journal')}
                                            {canManage && a.status !== 'PAID' && <Button data-help="commercial.b2bDetail.recordPayment" size="small" onClick={() => openPay(a)}>Record payment</Button>}
                                            {canManage && a.payments.length === 0 && (
                                                <Tooltip title="Delete acceptance (reverses its journal)">
                                                    <IconButton data-help="commercial.b2bDetail.deleteAcceptance" size="small" aria-label={`Delete acceptance ${a.billReference}`} disabled={busy}
                                                        onClick={() => run(() => B2bAccountingService.deleteAcceptance(institutionId, masterLcId, b2bId, a.id), 'Acceptance deleted')}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {a.payments.map((p, i) => (
                                        <TableRow key={p.id} sx={{ '& td': { color: 'text.secondary', borderBottom: 'none' } }}>
                                            <TableCell />
                                            <TableCell>Paid {formatDate(p.paymentDate)}</TableCell>
                                            <TableCell align="right">{formatMoney(p.foreignAmount, cur)}</TableCell>
                                            <TableCell align="right">{p.paymentRate}</TableCell>
                                            <TableCell align="right">{formatMoney(p.paidBase, 'BDT')}</TableCell>
                                            <TableCell colSpan={2}>
                                                FX {p.fxGainLossBase >= 0 ? 'gain' : 'loss'} {formatMoney(Math.abs(p.fxGainLossBase), 'BDT')}
                                                {p.bankCharges > 0 ? ` · charges ${formatMoney(p.bankCharges, 'BDT')}` : ''}
                                            </TableCell>
                                            <TableCell />
                                            <TableCell>
                                                {canViewJournal && journalLink(p.journalEntryId, 'Journal')}
                                                {canManage && i === a.payments.length - 1 && (
                                                    <Tooltip title="Delete payment (reverses its journal)">
                                                        <IconButton data-help="commercial.b2bDetail.deletePayment" size="small" aria-label={`Delete payment ${formatDate(p.paymentDate)}`} disabled={busy}
                                                            onClick={() => run(() => B2bAccountingService.deletePayment(institutionId, masterLcId, b2bId, a.id, p.id), 'Payment deleted')}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                            {acc.acceptances.length === 0 && (
                                <TableRow><TableCell colSpan={9} align="center">No acceptances recorded.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={marginOpen} onClose={() => setMarginOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Record LC margin</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField data-help="commercial.b2bDetail.marginAmount" label="Margin amount (BDT)" type="number" required fullWidth value={margin.marginAmount}
                        onChange={e => setMargin({ ...margin, marginAmount: e.target.value })} sx={{ mt: 1 }} />
                    <TextField data-help="commercial.b2bDetail.marginPercent" label="Margin %" type="number" fullWidth value={margin.marginPercent}
                        onChange={e => setMargin({ ...margin, marginPercent: e.target.value })} />
                    <TextField data-help="commercial.b2bDetail.marginDate" label="Date" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={margin.marginDate}
                        onChange={e => setMargin({ ...margin, marginDate: e.target.value })} />
                    <AccountAutocomplete label="Bank account" accounts={bankAccounts} value={margin.bankAccountId}
                        onChange={id => setMargin({ ...margin, bankAccountId: id })} placeholder="Use settings default bank"
                        helperText="Margin is released back to this account" fullWidth data-testid="margin-bank-account" />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.b2bDetail.marginCancel" onClick={() => setMarginOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.b2bDetail.marginSave" {...helpWhy(marginWhy ? { kind: 'status', message: marginWhy } : null)} variant="contained" disabled={!marginValid || busy} onClick={saveMargin}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={releaseOpen} onClose={() => setReleaseOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Release margin</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField data-help="commercial.b2bDetail.releaseDate" label="Release date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={releaseDate}
                        onChange={e => setReleaseDate(e.target.value)} sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.b2bDetail.releaseCancel" onClick={() => setReleaseOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.b2bDetail.releaseConfirm" {...helpWhy(releaseWhy ? { kind: 'status', message: releaseWhy } : null)} variant="contained" disabled={!releaseDate || busy}
                        onClick={() => run(() => B2bAccountingService.releaseMargin(institutionId, masterLcId, b2bId, releaseDate), 'Margin released', () => setReleaseOpen(false))}>Release</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={acceptOpen} onClose={() => setAcceptOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Record acceptance</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField data-help="commercial.b2bDetail.accRef" label="Document / bill reference" required fullWidth value={accept.billReference}
                        onChange={e => setAccept({ ...accept, billReference: e.target.value })} sx={{ mt: 1 }} />
                    <TextField data-help="commercial.b2bDetail.accDate" label="Acceptance date" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={accept.acceptanceDate}
                        onChange={e => setAccept({ ...accept, acceptanceDate: e.target.value })} />
                    <TextField data-help="commercial.b2bDetail.accAmount" label={`Amount (${cur})`} type="number" required fullWidth value={accept.foreignAmount}
                        onChange={e => setAccept({ ...accept, foreignAmount: e.target.value })} />
                    <TextField data-help="commercial.b2bDetail.accRate" label="Acceptance rate (BDT per unit)" type="number" required fullWidth value={accept.acceptanceRate}
                        onChange={e => setAccept({ ...accept, acceptanceRate: e.target.value })} />
                    <TextField data-help="commercial.b2bDetail.accUsance" label="Usance (days)" type="number" required fullWidth value={accept.usanceDays}
                        onChange={e => setAccept({ ...accept, usanceDays: e.target.value })}
                        helperText="Maturity = acceptance date + usance days" />
                    <TextField data-help="commercial.b2bDetail.accRemarks" label="Remarks" fullWidth value={accept.remarks} onChange={e => setAccept({ ...accept, remarks: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.b2bDetail.accCancel" onClick={() => setAcceptOpen(false)}>Cancel</Button>
                    <Button data-help="commercial.b2bDetail.accSave" {...helpWhy(accWhy ? { kind: 'status', message: accWhy } : null)} variant="contained" disabled={!acceptValid || busy} onClick={saveAcceptance}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Record payment — {payTarget?.billReference}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField data-help="commercial.b2bDetail.payDate" label="Payment date" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={pay.paymentDate}
                        onChange={e => setPay({ ...pay, paymentDate: e.target.value })} sx={{ mt: 1 }} />
                    <TextField data-help="commercial.b2bDetail.payAmount" label={`Amount (${cur})`} type="number" required fullWidth value={pay.foreignAmount}
                        onChange={e => setPay({ ...pay, foreignAmount: e.target.value })}
                        helperText={payTarget ? `Outstanding ${formatMoney(payTarget.outstandingForeignAmount, cur)}. Paying it all clears the liability.` : undefined} />
                    <TextField data-help="commercial.b2bDetail.payRate" label="Payment rate (BDT per unit)" type="number" required fullWidth value={pay.paymentRate}
                        onChange={e => setPay({ ...pay, paymentRate: e.target.value })}
                        helperText="The difference to the acceptance rate posts to FX gain / loss" />
                    <TextField data-help="commercial.b2bDetail.payCharges" label="Bank charges (BDT)" type="number" fullWidth value={pay.bankCharges}
                        onChange={e => setPay({ ...pay, bankCharges: e.target.value })} />
                    <AccountAutocomplete label="Pay from account" accounts={bankAccounts} value={pay.paymentAccountId}
                        onChange={id => setPay({ ...pay, paymentAccountId: id })} placeholder="Use settings default bank"
                        fullWidth data-testid="payment-account" />
                    <TextField data-help="commercial.b2bDetail.payRef" label="Reference" fullWidth value={pay.reference} onChange={e => setPay({ ...pay, reference: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="commercial.b2bDetail.payCancel" onClick={() => setPayTarget(null)}>Cancel</Button>
                    <Button data-help="commercial.b2bDetail.paySave" {...helpWhy(payWhy ? { kind: 'status', message: payWhy } : null)} variant="contained" disabled={!payValid || busy} onClick={savePayment}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default B2bLcDetail;
